import { LoadingOutlined, PlusOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Table, Tabs } from 'antd';
import axios from 'axios';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { PlayListNew } from '../../assets';
import { BASE_URL } from '../../constants/api-urls';
import {
  SPOTIFY_CODE,
  SPOTIFY_REFRESH_TOKEN,
  SPOTIFY_TOKEN,
  SPOTIFY_USER_INFO,
} from '../../constants/storage';
import { useLoginContext } from '../../context';
import { usePackageContext } from '../../context/PackageContext';
import { usePlaylistContext } from '../../context/PlaylistContext';
import {
  Auth0Logout,
  addItemToStorage,
  deleteItemFromStorage,
} from '../../utils';
import { isGoogleAuth0User } from '../../utils/isGoogleAuth0User';
import { fetchProfileOwnedPlaylists } from '../../services/curatorAccountService';
import { PasswordModel, UsernameModel } from './index';

import PricingModal from '../../pages/landingPage/PaymentModal';
import PlanToast from '../PlanToast/PlanToast';
import DeleteModal from '../Subscriptions/CancelSubscription';
import TeamManagementTab from './TeamManagementTab';
import { GeneralInformationFormStyle } from './GeneralInformationForm.style';
import AccountMfaSettings from '../AccountMfaSettings/AccountMfaSettings';
import PreferredBillingToggle from '../PreferredBillingToggle/PreferredBillingToggle';
import {
  getPreferredBillingErrorMessage,
  PreferredBillingMethod,
  upgradePlanWithCredits,
} from '../../services/preferredBillingService';
import { getStripePlanType, storeWalletPlan } from '../../utils/planType';
import { getCuratorAuthHeader } from '../../utils/curatorApiAuth';

const GeneralInformationForm = () => {
  // ==================== STATE MANAGEMENT ====================
  // User data states
  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem('userInfo')));
  const userId = user?.userId;

  // UI states
  const [defaultTab, setDefaultTab] = useState('1');
  const [theme, setTheme] = useState('dark');
  const [accountLoader, setAccountLoader] = useState(false);
  const [editDetailsLoading, setEditDetailsLoading] = useState(false);
  const [setNewLoading, showNewLoading] = useState(false);

  // Modal states
  const [princingModalIsOpen, setPrincingModalIsOpen] = useState<boolean>(false);
  const [isModalOpenDelete, setIsModalOpenDelete] = useState(false);
  const [isModalOpenRemoveAccount, setIsModalOpenRemoveAccount] = useState(false);

  // Preferred billing / wallet upgrade states
  const [preferredBillingMethod, setPreferredBillingMethod] = useState<PreferredBillingMethod>('card');
  const [creditsAllowed, setCreditsAllowed] = useState(false);
  const [walletUpgradeLoading, setWalletUpgradeLoading] = useState(false);
  const [walletUpgradeBanner, setWalletUpgradeBanner] = useState<string | null>(null);
  const [billingRefreshKey, setBillingRefreshKey] = useState(0);

  // Account and subscription states
  const [showAccount, setAccount] = useState([]);
  const [allLinkedAccounts] = useState([]);
  const [removeAccountProfile, setRemoveAccountProfile] = useState(null);
  const [setUserProfile, showUserProfie] = useState([]);
  const [subscriptionInfo, setSubscriptionInfo] = useState({
    cancelAt: 0,
    cancelAtPeriodEnd: false,
    status: '',
    trialEnd: 0,
    priceId: '',
    unitAmount: 0,
  });

  const [profilePlaylistCounts, setProfilePlaylistCounts] = useState<Record<string, number>>({});

  // Payment states
  const [cancelPaymentData, setCancelPaymentData] = useState(false);
  const [expire_date, setExpire_date] = useState('');
  const [cardDetail, setCardDetail] = useState({
    brand: '',
    checks: {
      address_line1_check: null,
      address_postal_code_check: null,
      cvc_check: '',
    },
    country: '',
    exp_month: 0,
    exp_year: 0,
    fingerprint: '',
    funding: '',
    generated_from: null,
    last4: '',
    networks: {
      available: [''],
      preferred: null,
    },
    three_d_secure_usage: {
      supported: true,
    },
    wallet: null,
  });
  const [dataSource, setDataSource] = useState([]);

  // ==================== CONTEXT HOOKS ====================
  const { dispatch } = useLoginContext();
  const { clearCache, refreshCache } = usePackageContext();
  const { state: playlistState } = usePlaylistContext();

  // ==================== FEATURES DATA ====================
  const features = [
    {
      featureName: 'Pre-release feature',
      availibilty: ['Unlimited'],
    },
    {
      featureName: 'Add or delete songs',
      availibilty: ['Free', 'Pro', 'Unlimited'],
    },
    {
      featureName: 'Create, edit or delete playlists',
      availibilty: ['Free', 'Pro', 'Unlimited'],
    },
    {
      featureName: 'Add notes',
      availibilty: ['Pro', 'Unlimited'],
    },
    {
      featureName: 'Song popularity index',
      availibilty: ['Pro', 'Unlimited'],
    },
    {
      featureName: 'Schedule start dates',
      availibilty: ['Pro', 'Unlimited'],
    },
    {
      featureName: 'Schedule end dates',
      availibilty: ['Pro', 'Unlimited'],
    },
    {
      featureName: 'Bulk mode',
      availibilty: ['Free', 'Pro', 'Unlimited'],
    },
    {
      featureName: 'Select multiple playlists at once',
      availibilty: ['Pro', 'Unlimited'],
    },
    {
      featureName: 'Show Songplace only',
      availibilty: ['Pro', 'Unlimited'],
    },
    {
      featureName: 'Search by pasting link',
      availibilty: ['Free', 'Pro', 'Unlimited'],
    },
    {
      featureName: 'Priority support',
      availibilty: ['Unlimited'],
    },
    {
      featureName: 'Reset song positions',
      availibilty: ['Unlimited'],
    },
    {
      featureName: 'Unlimited refresh playlists',
      availibilty: ['Unlimited'],
    },
  ];

  // ==================== EFFECTS ====================
  // Listen for localStorage changes and update user data
  useEffect(() => {
    const handleStorageChange = () => {
      const updatedUser = JSON.parse(localStorage.getItem('userInfo') || '{}');
      setUser(updatedUser);
    };

    // Listen for storage events (for cross-tab communication)
    window.addEventListener('storage', handleStorageChange);

    // Also check for changes periodically (for same-tab updates)
    const interval = setInterval(() => {
      const currentUser = JSON.parse(localStorage.getItem('userInfo') || '{}');
      if (JSON.stringify(currentUser) !== JSON.stringify(user)) {
        setUser(currentUser);
      }
    }, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [user]);

  // Theme effect
  useEffect(() => {
    setTheme(playlistState?.isWholeTheme === false ? 'dark' : 'light');
  }, [playlistState]);

  // User data effect
  useEffect(() => {
    if (userId) {
      newData(userId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, user?.userId]);

  // Payment methods effect
  useEffect(() => {
    if (showAccount && showAccount.length) {
      fetch(`${BASE_URL}/getUserPaymentMethods`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getCuratorAuthHeader(),
        },
        body: JSON.stringify({
          customerId: showAccount[showAccount.length - 1].payment_status.customer,
        }),
      })
        .then(response => response.json())
        .then(result => {
          setCardDetail(result.data.card);
        })
        .catch(error => console.log('error', error));
    }
  }, [showAccount]);

  // Expire date calculation effect
  useEffect(() => {
    if (showAccount.length) {
      var month = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      let startDate = new Date(
        showAccount[showAccount.length - 1]?.payment_status.period_end * 1000
      );

      if (user?.duration === 'month') {
        let endDate = new Date(startDate).setMonth(new Date(startDate).getMonth() + 1);

        let date = `${new Date(endDate).getDate()} ${
          month[new Date(endDate).getMonth()]
        } ${new Date(endDate).getFullYear()}`;
        setExpire_date(date);
      } else {
        let endDate = new Date(startDate).setFullYear(new Date(startDate).getFullYear() + 1);

        let date = `${new Date(endDate).getDate()} ${
          month[new Date(endDate).getMonth()]
        } ${new Date(endDate).getFullYear()}`;
        setExpire_date(date);
      }

      let history = [];

      history = [...showAccount].reverse().map((e, index) => {
        const d = new Date(e?.payment_status?.period_end * 1000);
        const planInfo = getPlanInfo(e?.payment_status);
        return {
          date: <p>{moment(d).format('lll')}</p>,
          amount: (
            <p style={{ textTransform: 'uppercase' }}>
              {/* eslint-disable-next-line no-useless-concat */}
              {e?.payment_status?.subtotal / 100 + '.00' + ' ' + e?.payment_status?.currency}
            </p>
          ),
          status: (
            <p
              style={{
                color:
                  e?.payment_status?.status === 'active'
                    ? 'green'
                    : e?.payment_status?.status === 'canceled'
                      ? 'red'
                      : 'orange',
              }}
            >
              {planInfo.name}
            </p>
          ),
        };
      });

      setDataSource(history);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAccount, user?.duration, playlistState]);

  useEffect(() => {
    if (!setUserProfile?.length || !userId) {
      setProfilePlaylistCounts({});
      return;
    }

    let cancelled = false;

    const loadProfilePlaylistCounts = async () => {
      const entries = await Promise.all(
        setUserProfile.map(async profile => {
          const profileId = profile?._id;
          if (!profileId) {
            return [profileId, 0] as const;
          }

          try {
            const playlists = await fetchProfileOwnedPlaylists(profileId, userId);
            return [profileId, playlists.length] as const;
          } catch {
            return [profileId, 0] as const;
          }
        })
      );

      if (!cancelled) {
        setProfilePlaylistCounts(Object.fromEntries(entries.filter(([profileId]) => profileId)));
      }
    };

    loadProfilePlaylistCounts();

    return () => {
      cancelled = true;
    };
  }, [setUserProfile, userId]);

  // Handle URL parameter changes for tab navigation
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    if (tab === 'upgradeplan') {
      setDefaultTab('3');
      setPrincingModalIsOpen(true);
    } else if (tab && ['1', '2', '3', '4'].includes(tab)) {
      setDefaultTab(tab);
    }
  }, []); // Empty dependency array - only run on mount

  const refreshBillingFromStripe = (
    priceId: string,
    subscriptionId?: string,
    customerId?: string
  ) => {
    const current = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const nextUser = {
      ...current,
      free: false,
      planId: priceId,
      ...(subscriptionId ? { subId: subscriptionId } : {}),
      ...(customerId ? { custId: customerId } : {}),
    };
    localStorage.setItem('userInfo', JSON.stringify(nextUser));
    setUser(prev => ({ ...(prev || {}), ...nextUser }));
    storeWalletPlan(null);
    setBillingRefreshKey(key => key + 1);
  };

  const handleCreditsPlanChange = async (priceId: string, type: string, currency?: string) => {
    if (!userId) {
      toast.error('Missing user id for credits upgrade.');
      return;
    }
    setWalletUpgradeBanner(null);
    setWalletUpgradeLoading(true);
    try {
      // Subscribers get their Stripe price changed; curators with no subscription get a
      // new one. Either way the invoice is paid from wallet credits.
      const result = await upgradePlanWithCredits(userId, priceId, currency);
      refreshBillingFromStripe(
        result.priceId || priceId,
        result.subscriptionId,
        result.customerId
      );
      setSubscriptionInfo(prev => ({
        ...prev,
        cancelAt: 0,
        cancelAtPeriodEnd: false,
        priceId: result.priceId || priceId,
        status: 'active',
      }));
      setPrincingModalIsOpen(false);
      toast.success(
        result.alreadyOnPlan
          ? 'You are already on this plan.'
          : `Subscribed to ${type} — ${result.amountCharged.toFixed(2)} ${result.currencyCode} taken from wallet credits.`
      );
    } catch (error) {
      setWalletUpgradeBanner(
        getPreferredBillingErrorMessage(error, 'Could not pay for your plan from wallet credits.')
      );
    } finally {
      setWalletUpgradeLoading(false);
    }
  };

  const handlePricingSelection = (id: string, type: string, duration: string, currency: string) => {
    setPrincingModalIsOpen(false);
    if (type === 'Free') {
      window.location.href = `${window.location.origin}/created?session_id=free`;
      let userInfo = JSON.parse(localStorage.getItem('userInfo'));
      let updatedUser = {
        ...userInfo,
        planId: 'free',
        free: true,
        duration: duration,
      };
      getFirstConnected();
      localStorage.setItem('userInfo', JSON.stringify(updatedUser));
    } else {
      fetch(`${BASE_URL}/stripe-payment-form`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getCuratorAuthHeader(),
        },
        body: JSON.stringify({
          successUrl: `${window.location.origin}/created?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: window.location.href,
          email: user?.email,
          subId: user?.userId,
          product_id: id,
          currency: currency.toLowerCase(),
          custId: user?.custId ? `${user?.custId}` : '',
        }),
      })
        .then(async res => {
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            toast.error(data?.message || 'Could not start checkout. Please try again.');
            return;
          }
          getFirstConnected();
          const checkoutUrl = data?.data?.url;
          if (typeof checkoutUrl === 'string' && checkoutUrl) {
            window.location.href = checkoutUrl;
            return;
          }
          toast.error('Could not start checkout. Please try again.');
        })
        .catch(() => {
          toast.error('Could not start checkout. Please try again.');
        });
    }
  };

  const showModalDelete = obj => {
    setIsModalOpenDelete(true);
    setCancelPaymentData(obj);
  };

  const handleOkDelete = logout => {
    setIsModalOpenDelete(false);
    cancelPackage(cancelPaymentData ? false : logout, cancelPaymentData);
    setCancelPaymentData(false);
  };

  const handleCancelDelete = () => {
    setIsModalOpenDelete(false);
    setCancelPaymentData(false);
  };

  const showModalRemoveAccount = c => {
    setIsModalOpenRemoveAccount(true);
    setRemoveAccountProfile(c);
  };

  const handleOkRemoveAccount = () => {
    setIsModalOpenRemoveAccount(false);
    deleteHandler(removeAccountProfile?._id, removeAccountProfile);
  };

  const handleCancelRemoveAccount = () => {
    setIsModalOpenRemoveAccount(false);
    setRemoveAccountProfile(null);
  };

  useEffect(() => {
    if (!user?.custId) {
      showNewLoading(false);
      setAccount([]);
      return;
    }

    showNewLoading(true);
    fetch(`${BASE_URL}/get/packages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getCuratorAuthHeader(),
      },
      body: JSON.stringify({
        email: `${user?.email ? user?.email : user?.email}`,
        userId: user?.userId,
        custId: user?.custId,
      }),
    })
      .then(res => res.json())
      .then(data => {
        if (data) {
          showNewLoading(false);
          const resData = data?.data.reverse();
          setAccount(resData);

          if (
            resData &&
            resData.length > 0 &&
            resData[resData.length - 1]?.payment_status.subscription
          ) {
            fetch(`${BASE_URL}/getUserSubscription`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...getCuratorAuthHeader(),
              },
              body: JSON.stringify({
                subId: resData[resData.length - 1]?.payment_status.subscription,
              }),
            })
              .then(res => res.json())
              .then(subData => {
                if (subData?.body) {
                  const price = subData.body?.items?.data?.[0]?.price;
                  const priceId = price?.id || subData.body?.plan?.id || '';
                  const unitAmount = Number(price?.unit_amount || subData.body?.plan?.amount || 0);
                  setSubscriptionInfo({
                    cancelAt: subData?.body?.cancel_at || 0,
                    cancelAtPeriodEnd: subData?.body?.cancel_at_period_end || false,
                    status: subData?.body?.status || '',
                    trialEnd: subData?.body?.trial_end || 0,
                    priceId,
                    unitAmount,
                  });
                  if (
                    (subData.body.status === 'active' || subData.body.status === 'trialing') &&
                    priceId
                  ) {
                    // Stripe subscription is source of truth for billing UI — drop leftover
                    // walletPlan grants that incorrectly marked Unlimited after credits upgrades.
                    storeWalletPlan(null);
                    const current = JSON.parse(localStorage.getItem('userInfo') || '{}');
                    localStorage.setItem(
                      'userInfo',
                      JSON.stringify({
                        ...current,
                        free: false,
                        planId: priceId,
                        subId: subData.body.id || current.subId,
                        custId: subData.body.customer || current.custId,
                      })
                    );
                    setUser(prev => ({
                      ...(prev || {}),
                      free: false,
                      planId: priceId,
                      subId: subData.body.id || prev?.subId,
                      custId: subData.body.customer || prev?.custId,
                    }));
                  }
                }
              })
              .catch(err => {
                console.log('Error in fetching subcription info');
              });
          }
        }
      })
      .catch(error => {
        showNewLoading(false);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.custId, user?.userId, billingRefreshKey]); // Re-fetch when customer/user changes or after credits upgrade

  //Get User Profile
  const newData = async user_Id => {
    try {
      setAccountLoader(true);
      const config = {
        headers: {
          'Content-Type': 'application-json',
        },
      };
      const response = await axios.get(`${BASE_URL}/user/${user_Id}/data`, config);
      if (response) {
        setAccountLoader(false);
        showUserProfie(response?.data?.data);
      }
    } catch (error) {
      setAccountLoader(false);
    }
  };

  const redirectToPaymentPortal = customerId => {
    // Add validation for customerId
    if (!customerId) {
      console.error('Customer ID is missing');
      toast.error('Unable to edit details: Customer information not found');
      return;
    }

    const redirectUrl = `${window.location.origin}/home`;
    fetch(`${BASE_URL}/packages/details/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getCuratorAuthHeader(),
      },
      body: JSON.stringify({
        customerId: `${customerId}`,
        redirectUrl: `${redirectUrl}`,
      }),
    })
      .then(res => res.json())
      .then(data => {
        // Validate the response structure
        if (data?.data?.url) {
          window.location.replace(data.data.url);
        } else {
          console.error('Invalid API response structure:', data);
          toast.error('Unable to redirect to payment portal. Please try again later.');
        }
      })
      .catch(error => {
        console.error('API call failed:', error);
        toast.error('Failed to access payment portal. Please try again later.');
      });
  };

  const deleteHandler = async (id, account) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application-json',
        },
      };
      const response = await axios.post(`${BASE_URL}/user/${id}/delete`, config);
      if (response) {
        try {
          newData(userId);
          setRemoveAccountProfile(false);
          window.location.reload();
        } catch (error) {}
      }
    } catch (error) {}
  };

  const onChangeed = () => {
    import('../../utils/spotifyAuth').then(({ buildSpotifyAuthorizeUrl }) => {
      window.location.replace(
        buildSpotifyAuthorizeUrl({
          showDialog: true,
          mode: 'add_profile',
        })
      );
    });
  };

  const changeHandler = (token: any, user: any, refresh: any, code: any, image: any) => {
    addItemToStorage(SPOTIFY_CODE, code);
    deleteItemFromStorage(SPOTIFY_TOKEN);
    deleteItemFromStorage(SPOTIFY_REFRESH_TOKEN);
    const data = {
      username: user,
      image: image,
      code: token,
      userId: userId,
    };
    localStorage.setItem('spotifyUser', JSON.stringify(data, null, ' '));
    if (token && refresh) {
      addItemToStorage(SPOTIFY_TOKEN, token);

      addItemToStorage(SPOTIFY_REFRESH_TOKEN, refresh);

      dispatch({
        type: 'ADD_APP_TOKEN',
        payload: {
          token: token,
          refreshToken: refresh,
        },
      });
      window.location.replace('/home');
    }
  };

  const onBack = () => {
    deleteItemFromStorage(SPOTIFY_TOKEN);
    deleteItemFromStorage(SPOTIFY_CODE);
    deleteItemFromStorage(SPOTIFY_USER_INFO);
    localStorage.removeItem('userInfo');
    localStorage.removeItem('spotifyUser');

    // Clear package cache
    clearCache();

    Auth0Logout();
  };

  const cancelPackage = (logout, obj) => {
    const userInfo = JSON.parse(localStorage.getItem('userInfo'));
    if (userInfo) {
      // If obj is provided, user is switching plans (not just cancelling)
      // In this case, we need to proceed to the new plan selection
      if (obj && obj.id) {
        // User is upgrading/switching plans - proceed with plan change
        handlePricingSelection(obj.id, obj.type, obj.duration, obj.currency);
        return;
      }

      // User is cancelling subscription - use cancelAtPeriodEnd to preserve access
      // This allows users to keep access until their paid period ends
      fetch(`${BASE_URL}/packages/cancelAtPeriodEnd`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getCuratorAuthHeader(),
        },
        body: JSON.stringify({
          subscription:
            showAccount &&
            showAccount.length > 0 &&
            showAccount[showAccount.length - 1]?.payment_status.subscription,
          payment: 'null',
        }),
      })
        .then(res => res.json())
        .then(data => {
          if (data.success && data.subscriptionInfo) {
            // Update local state to show pending cancellation
            setSubscriptionInfo(prev => ({
              ...prev,
              cancelAt: data.subscriptionInfo.cancel_at || data.subscriptionInfo.current_period_end,
              cancelAtPeriodEnd: true,
              status: data.subscriptionInfo.status || '',
              trialEnd: data.subscriptionInfo.trial_end || 0,
            }));

            // Calculate and show the end date to user
            const endTimestamp =
              data.subscriptionInfo.cancel_at || data.subscriptionInfo.current_period_end;
            const endDate = new Date(endTimestamp * 1000);
            const formattedDate = endDate.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            });

            toast.success(
              `Your subscription has been cancelled. You will retain full access until ${formattedDate}. You will not be charged again.`
            );

            // Refresh package cache to update UI
            refreshCache();

            // DO NOT clear metadata - subscription is still active until period end
            // DO NOT logout - user keeps access until period end
          } else {
            toast.error('Failed to cancel subscription. Please try again.');
          }
        })
        .catch(error => {
          console.error('Error cancelling subscription:', error);
          toast.error('Failed to cancel subscription. Please try again.');
        });
    } else {
      window.location.replace('/');
    }
  };

  const columns = [
    {
      title: '',
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: '',
      dataIndex: 'amount',
      key: 'amount',
    },
    {
      title: '',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: '',
      dataIndex: 'website',
      key: 'website',
    },
  ];
  let { monthly_pro_price, annually_pro_price, monthly_unlimited_price, annually_unlimited_price } =
    playlistState;
  let proPlanMonthly = process.env.REACT_APP_STRIPE_PRO_MONTHLY;
  let proPlan = process.env.REACT_APP_STRIPE_PRO;
  let unlimitedPlanMonthly = process.env.REACT_APP_STRIPE_UNLIMITED_MONTHLY;
  let unlimitedPlan = process.env.REACT_APP_STRIPE_UNLIMITED;

  // Helper function to determine plan info for trial and non-trial users
  const planFromPriceId = priceId => {
    if (!priceId) return null;
    if (priceId === proPlanMonthly) return { name: 'Pro', isMonthly: true, isPro: true };
    if (priceId === proPlan) return { name: 'Pro', isMonthly: false, isPro: true };
    if (priceId === unlimitedPlanMonthly) {
      return { name: 'Unlimited', isMonthly: true, isPro: false };
    }
    if (priceId === unlimitedPlan) return { name: 'Unlimited', isMonthly: false, isPro: false };
    return null;
  };

  const getPlanInfo = paymentStatus => {
    const fromSubscription =
      planFromPriceId(subscriptionInfo.priceId) || planFromPriceId(user?.planId);
    if (fromSubscription) return fromSubscription;

    // For non-trial users, use existing subtotal matching logic
    const amount = Number(paymentStatus?.subtotal);
    if (monthly_pro_price.includes(amount) || annually_pro_price.includes(amount)) {
      return {
        name: 'Pro',
        isMonthly: monthly_pro_price.includes(amount),
        isPro: true,
      };
    } else if (
      annually_unlimited_price.includes(amount) ||
      monthly_unlimited_price.includes(amount)
    ) {
      return {
        name: 'Unlimited',
        isMonthly: monthly_unlimited_price.includes(amount),
        isPro: false,
      };
    }

    return { name: 'Free', isMonthly: true, isPro: false };
  };

  // Helper function to get plan price for display
  const getPlanPrice = paymentStatus => {
    const plan = planFromPriceId(subscriptionInfo.priceId) || planFromPriceId(user?.planId);
    if (plan && subscriptionInfo.unitAmount > 0) {
      return plan.isMonthly ? subscriptionInfo.unitAmount / 100 : subscriptionInfo.unitAmount / 100 / 12;
    }
    if (plan) {
      if (plan.name === 'Pro' && plan.isMonthly && monthly_pro_price.length > 0) {
        return monthly_pro_price[0] / 100;
      }
      if (plan.name === 'Pro' && !plan.isMonthly && annually_pro_price.length > 0) {
        return annually_pro_price[0] / 100 / 12;
      }
      if (plan.name === 'Unlimited' && plan.isMonthly && monthly_unlimited_price.length > 0) {
        return monthly_unlimited_price[0] / 100;
      }
      if (plan.name === 'Unlimited' && !plan.isMonthly && annually_unlimited_price.length > 0) {
        return annually_unlimited_price[0] / 100 / 12;
      }
    }

    // For non-trial users, use existing logic
    const amount = Number(paymentStatus?.subtotal);
    if (monthly_pro_price.includes(amount) || monthly_unlimited_price.includes(amount)) {
      return amount / 100;
    } else if (annually_unlimited_price.includes(amount) || annually_pro_price.includes(amount)) {
      return amount / 100 / 12;
    }

    return 0;
  };
  const AddAccountComp = ({ blur }) => {
    return (
      <div className="accountDetailBox accountDetailBoxTransparrent">
        <div
          style={{
            textAlign: 'center',
          }}
        >
          <p
            style={{
              color: '#6c6c6c',
              textAlign: 'center',
            }}
          >
            Want to add another account?
          </p>
          {blur ? (
            <PlanToast available={[]} titleHeading={'Add account'}>
              <button
                className="btnns3"
                onClick={() => {
                  if (!blur) {
                    onChangeed();
                  }
                }}
              >
                <PlusOutlined /> Add account
              </button>
            </PlanToast>
          ) : (
            <button
              className="btnns3"
              onClick={() => {
                if (!blur) {
                  onChangeed();
                }
              }}
            >
              <PlusOutlined /> Add account
            </button>
          )}
        </div>
      </div>
    );
  };
  const getFirstConnected = () => {
    if (allLinkedAccounts && allLinkedAccounts.length) {
      let firstAccount = allLinkedAccounts.sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      )[0];
      changeHandler(
        firstAccount?.token,
        firstAccount?.username,
        firstAccount?.refresh,
        firstAccount?.code,
        firstAccount?.image
      );
    }
  };

  return (
    <GeneralInformationFormStyle>
      <>
        <div className="GeneralInformationFormStyleclassName">
          <Tabs
            activeKey={defaultTab}
            onChange={key => setDefaultTab(key)}
            className="tabs"
            items={[
              {
                label: 'Account overview',
                key: '1',
                children: (
                  <div className="row-space row-space-profile">
                    <div className="profile">
                      <img
                        alt="account cover"
                        src="./static/cover_image.png"
                        className="coverImageAccountinfo"
                      />
                      <div className="row row-profile-FormStyle">
                        <Avatar
                          className="avatar Profile-View-Span"
                          icon={
                            user?.profileImage ? (
                              <img alt="userProfile" src={user?.profileImage} />
                            ) : (
                              <UserOutlined />
                            )
                          }
                        />
                      </div>
                      <div>
                        <p className="po5">{user?.username}</p>
                        <div className="divider1"></div>
                        <div className="row-profile-FormStyle">
                          <div className="divide">
                            <p className="po4">
                              <span className="fontsizespanpo4" style={{ fontWeight: '700' }}>
                                Profile name :{' '}
                              </span>
                              <span className="spann fontsizespanpo4">{user?.username} </span>
                              {isGoogleAuth0User(user?.userId) ? null : (
                                <UsernameModel username={user?.username} />
                              )}
                            </p>
                            <p className="po3">
                              <span className="fontsizespanpo4" style={{ fontWeight: '700' }}>
                                Email :{' '}
                              </span>
                              <span className="spann fontsizespanpo4">{user?.email}</span>
                            </p>
                            <p className="pop1">
                              <span className="fontsizespanpo4" style={{ fontWeight: '700' }}>
                                Password :{' '}
                              </span>
                              <span
                                className="spanner fontsizespanpo4"
                                style={{ marginLeft: '5px' }}
                              >
                                *************
                                {isGoogleAuth0User(user?.userId) ? '' : <PasswordModel />}
                              </span>
                            </p>
                          </div>
                        </div>
                        <div className="divs2"></div>
                        <div className="row logoutButton">
                          <button className="btn1" onClick={onBack}>
                            {theme === 'dark' ? (
                              <img
                                alt="/Logout.png"
                                src="./static/Logout.png"
                                style={{ marginRight: '2%' }}
                              />
                            ) : (
                              <img
                                alt="/logout3.png"
                                src="./static/logout3.png"
                                style={{ marginRight: '2%', height: '22px' }}
                              />
                            )}
                            Logout account
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="profile1">
                      <div className="row">
                        <div className="div-90">
                          <h2 className="h2">Account details</h2>
                          <div className="rowing">
                            <div className="div">
                              <div
                                style={{
                                  textAlign: 'center',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                }}
                              >
                                <div className="svgDiv">
                                  <img alt="globe" src="./static/globe.png" />
                                </div>
                                <p className="ppo">Country</p>
                                {showAccount &&
                                  showAccount.length > 0 &&
                                  showAccount.slice(-1).map((e, index) => {
                                    return (
                                      <h5 className="h5" key={index}>
                                        {e?.payment_status?.customer_address?.country
                                          ? e?.payment_status?.customer_address?.country
                                          : 'no billing country'}
                                      </h5>
                                    );
                                  })}
                              </div>
                            </div>
                            <div className="div">
                              <div
                                style={{
                                  textAlign: 'center',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                }}
                              >
                                <div className="svgDiv">
                                  <img alt="paid" src="./static/paid.png" />
                                </div>
                                <p className="p3">Status</p>
                                <h5 className="h5">
                                  {user?.free
                                    ? 'Free'
                                    : setNewLoading === true
                                      ? 'loading..'
                                      : user?.planId === proPlan || user?.planId === proPlanMonthly
                                        ? 'Pro'
                                        : user?.planId === unlimitedPlan ||
                                            user?.planId === unlimitedPlanMonthly
                                          ? 'Unlimited'
                                          : 'Free'}
                                </h5>
                              </div>
                            </div>

                            <div className="div">
                              <div
                                style={{
                                  textAlign: 'center',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                }}
                              >
                                <div className="svgDiv">
                                  <img alt="verified" src="./static/verified.png" />
                                </div>
                                <p className="ppo">Email verification</p>
                                <h5 className="h5">
                                  {user
                                    ? user?.emailVerified === true
                                      ? 'Verified'
                                      : 'Not verified'
                                    : ''}
                                </h5>
                              </div>
                            </div>
                            <AccountMfaSettings userId={user?.userId} email={user?.email} />
                            {user?.free ? (
                              ''
                            ) : (
                              <div className="div">
                                <div
                                  style={{
                                    textAlign: 'center',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                  }}
                                >
                                  <div className="svgDiv">
                                    <img alt="discount" src="./static/discount.png" />
                                  </div>
                                  <p className="ppo">Discount</p>
                                  {setNewLoading === true ? (
                                    <h5 className="h5">loading..</h5>
                                  ) : (
                                    showAccount &&
                                    showAccount.length > 0 &&
                                    showAccount.slice(-1).map((e, index) => {
                                      const number = Number(
                                        e?.payment_status?.total_discount_amounts[0]?.amount / 100
                                      );
                                      return (
                                        <h5 className="h5" key={index}>
                                          {number}.00{' '}
                                          <span style={{ textTransform: 'uppercase' }}>
                                            {e?.payment_status?.currency}
                                          </span>
                                        </h5>
                                      );
                                    })
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="divider3">
                        <hr className="hr-90" />
                      </div>
                      <div className="row">
                        <div className="diver23">
                          <h2 className="hoo2">Account activity</h2>
                          <div className="rowing2">
                            <div className="div">
                              <div
                                style={{
                                  textAlign: 'center',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexDirection: 'column',
                                }}
                              >
                                <div className="svgDiv">
                                  <img alt="Logout1" src="./static/Logout1.png" />
                                </div>
                                <p className="poo1">Total logins</p>
                                <h5 className="h5">{user ? user?.allLogin : 0}</h5>
                              </div>
                            </div>
                            <div className="div">
                              <div
                                style={{
                                  textAlign: 'center',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexDirection: 'column',
                                }}
                              >
                                <div className="svgDiv">
                                  <img alt="calander" src="./static/calander.png" />
                                </div>
                                <p className="poo1">Last login</p>
                                <h5 className="h5">
                                  {user ? moment(user?.lastLogin).format('DD-MM-YYYY') : 'No Login'}
                                </h5>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                label: 'Linked profiles',
                key: '2',
                children: (
                  <div className="profile2">
                    <div className="row">
                      <div className="diver2">
                        <h2 className="htp2">Linked profiles</h2>
                        {accountLoader ? (
                          <div className="accountLoader">
                            <LoadingOutlined style={{ fontSize: 28 }} spin />
                          </div>
                        ) : (
                          <div className="rowing1">
                            {setUserProfile &&
                              setUserProfile?.length > 0 &&
                              setUserProfile
                                ?.sort(
                                  (a, b) =>
                                    new Date(a.created_at).getTime() -
                                    new Date(b.created_at).getTime()
                                )
                                ?.map(c => {
                                  return (
                                    <div
                                      style={{ cursor: 'pointer' }}
                                      className={`accountDetailBox`}
                                      onClick={() => {
                                        changeHandler(
                                          c?.token,
                                          c?.username,
                                          c?.refresh,
                                          c?.code,
                                          c?.image
                                        );
                                      }}
                                    >
                                      <div className="nameImage">
                                        <div className="diver4">
                                          <Avatar
                                            className="avater1 account-span"
                                            icon={
                                              !c?.image ? (
                                                <span className="spanner3">
                                                  {c?.username?.length >= 2 ? (
                                                    c?.username[0] + c?.username[1]
                                                  ) : (
                                                    <img
                                                      alt="mini-logo"
                                                      src={c?.image}
                                                      className="images2"
                                                    />
                                                  )}
                                                </span>
                                              ) : c?.image === 'dummy' ? (
                                                <span className="spanner3">
                                                  {c?.username?.length >= 2 ? (
                                                    c?.username[0] + c?.username[1]
                                                  ) : (
                                                    <img
                                                      alt="mini-logo"
                                                      src="./static/mini-logo.png"
                                                      className="images2"
                                                    />
                                                  )}
                                                </span>
                                              ) : (
                                                <img alt="profile" src={c?.image} />
                                              )
                                            }
                                          />
                                        </div>
                                        <div>
                                          <p className="ppp1" onClick={() => {}}>
                                            {c?.username}
                                          </p>
                                          <p className="ppp1 playListNumbers">
                                            {theme === 'dark' ? (
                                              <PlayListNew color={'white'} />
                                            ) : (
                                              <PlayListNew color={'#1D1D1D'} />
                                            )}{' '}
                                            {profilePlaylistCounts[c._id] ?? 0} Playlists in total
                                          </p>
                                        </div>
                                      </div>

                                      <div>
                                        {c?.username ===
                                        JSON.parse(localStorage.getItem('spotifyUser'))
                                          ?.username ? (
                                          <button className="btnns1">
                                            <svg
                                              className="ticIcon"
                                              width="16"
                                              height="16"
                                              viewBox="0 0 16 16"
                                              fill="none"
                                              xmlns="http://www.w3.org/2000/svg"
                                            >
                                              <path
                                                d="M2.75 8.75L6.25 12.25L13.25 4.75"
                                                stroke="#56BA32"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                            </svg>
                                          </button>
                                        ) : (
                                          <button
                                            className="btnns"
                                            onClick={e => {
                                              e.stopPropagation();
                                              showModalRemoveAccount(c);
                                            }}
                                          >
                                            <img alt="delete" src="./static/delete.png" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}

                            <AddAccountComp blur={false} />
                          </div>
                        )}
                      </div>
                    </div>
                    <DeleteModal
                      handleCancel={handleCancelRemoveAccount}
                      showModal={showModalRemoveAccount}
                      isModalOpen={isModalOpenRemoveAccount}
                      handleOk={handleOkRemoveAccount}
                      heading={'Delete profile'}
                      headingText={'Are you sure you want to delete this profile?'}
                      positionWarning={''}
                      button1={'Yes, delete'}
                      button2={'Cancel'}
                    />
                  </div>
                ),
              },
              {
                label: 'Team management',
                key: '4',
                children: <TeamManagementTab />,
              },
              {
                label: 'Billing',
                key: '3',
                children: (
                  <div className="profile2">
                    <div className="row">
                      <div className="divider21">
                        <div className="row-space">
                          <h2 className="htp2">Billing</h2>

                          {showAccount &&
                            showAccount.length > 0 &&
                            showAccount.slice(-1).map((e, index) => {
                              if (e?.payment_status?.customer) {
                                return (
                                  <button
                                    className="btnss1"
                                    onClick={() => {
                                      setEditDetailsLoading(true);
                                      redirectToPaymentPortal(e?.payment_status?.customer);
                                    }}
                                    disabled={editDetailsLoading}
                                  >
                                    {editDetailsLoading ? (
                                      <>
                                        <LoadingOutlined style={{ marginRight: 8 }} />
                                        Loading...
                                      </>
                                    ) : (
                                      'Update payment details'
                                    )}
                                  </button>
                                );
                              } else {
                                return '';
                              }
                            })}
                        </div>
                        <PreferredBillingToggle
                          userId={userId}
                          onChange={(method, meta) => {
                            setPreferredBillingMethod(method);
                            if (meta) setCreditsAllowed(meta.creditsAllowed);
                          }}
                        />
                        {setNewLoading ? (
                          <div className="accountLoader">
                            <LoadingOutlined style={{ fontSize: 28 }} spin />
                          </div>
                        ) : (
                          <>
                            <div className="rowing1">
                              <div className="divider22">
                                <h5 className="htp5">Current plan</h5>
                                <div className="planMainDiv">
                                  <div className="planDiv">
                                    <div className="planName">
                                      <div className="name">
                                        {user?.free
                                          ? 'Free'
                                          : showAccount &&
                                            showAccount.length > 0 &&
                                            showAccount.slice(-1).map((e, index) => {
                                              const planInfo = getPlanInfo(e?.payment_status);
                                              return planInfo.name;
                                            })}
                                      </div>
                                    </div>
                                    <div className="planDetail">
                                      {user?.free ? (
                                        <h2>Free</h2>
                                      ) : (
                                        showAccount &&
                                        showAccount.length > 0 &&
                                        showAccount.slice(-1).map((e, index) => {
                                          const planInfo = getPlanInfo(e?.payment_status);
                                          return <h2>{planInfo.name}</h2>;
                                        })
                                      )}
                                      <p className="perMonth">
                                        {user?.free ? (
                                          <span className="h5">Free </span>
                                        ) : (
                                          <>
                                            {showAccount &&
                                              showAccount.length > 0 &&
                                              showAccount.slice(-1).map((e, index) => {
                                                const price = getPlanPrice(e?.payment_status);
                                                return (
                                                  <>
                                                    <span className="h5">
                                                      {Math.round(price) + '.00'}

                                                      <span
                                                        style={{
                                                          textTransform: 'uppercase',
                                                        }}
                                                      >
                                                        {' '}
                                                        {e?.payment_status?.currency}
                                                      </span>
                                                    </span>{' '}
                                                    Per month
                                                  </>
                                                );
                                              })}{' '}
                                          </>
                                        )}{' '}
                                        {/* Per month */}
                                      </p>
                                      {user?.free ? (
                                        <div className="includes">
                                          <p className="includeHeading">Includes:</p>
                                          <div className="includesDiv">
                                            {features?.map(
                                              (b, j) =>
                                                b.availibilty.includes('Free') && (
                                                  <div className="includeDetail">
                                                    <svg
                                                      width="18"
                                                      height="18"
                                                      viewBox="0 0 18 18"
                                                      fill="none"
                                                      xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                      <circle cx="9" cy="9" r="9" fill="#1ED760" />
                                                      <path
                                                        d="M13.5382 5.61448C13.471 5.54664 13.3909 5.4928 13.3027 5.45605C13.2145 5.41931 13.1199 5.40039 13.0244 5.40039C12.9288 5.40039 12.8342 5.41931 12.746 5.45605C12.6578 5.4928 12.5778 5.54664 12.5105 5.61448L7.11848 11.0137L4.85311 8.74113C4.78325 8.67365 4.70078 8.62058 4.61042 8.58497C4.52005 8.54936 4.42356 8.5319 4.32644 8.53358C4.22933 8.53526 4.1335 8.55605 4.04442 8.59476C3.95534 8.63348 3.87476 8.68936 3.80727 8.75922C3.73979 8.82908 3.68673 8.91155 3.65112 9.00191C3.6155 9.09228 3.59804 9.18877 3.59972 9.28589C3.6014 9.383 3.62219 9.47884 3.66091 9.56792C3.69963 9.65699 3.75551 9.73758 3.82537 9.80506L6.60461 12.5843C6.6719 12.6521 6.75194 12.706 6.84014 12.7427C6.92834 12.7795 7.02294 12.7984 7.11848 12.7984C7.21403 12.7984 7.30863 12.7795 7.39682 12.7427C7.48502 12.706 7.56507 12.6521 7.63235 12.5843L13.5382 6.67841C13.6117 6.61063 13.6703 6.52838 13.7104 6.43682C13.7505 6.34527 13.7713 6.2464 13.7713 6.14644C13.7713 6.04649 13.7505 5.94762 13.7104 5.85607C13.6703 5.76451 13.6117 5.68225 13.5382 5.61448Z"
                                                        fill="white"
                                                      />
                                                    </svg>

                                                    {b.featureName}
                                                  </div>
                                                )
                                            )}
                                          </div>
                                        </div>
                                      ) : (
                                        showAccount &&
                                        showAccount.length > 0 &&
                                        showAccount.slice(-1).map((e, index) => {
                                          const amount = Number(e?.payment_status?.subtotal);
                                          return (
                                            <div className="includes">
                                              <p className="includeHeading">Includes:</p>
                                              <div className="includesDiv">
                                                {features?.map(
                                                  (b, j) =>
                                                    b.availibilty.includes(
                                                      user?.free
                                                        ? 'Free'
                                                        : monthly_pro_price.includes(amount) ||
                                                            annually_pro_price.includes(amount)
                                                          ? 'Pro'
                                                          : annually_unlimited_price.includes(
                                                                amount
                                                              ) ||
                                                              monthly_unlimited_price.includes(
                                                                amount
                                                              )
                                                            ? 'Unlimited'
                                                            : 'Free'
                                                    ) && (
                                                      <div className="includeDetail">
                                                        <svg
                                                          width="18"
                                                          height="18"
                                                          viewBox="0 0 18 18"
                                                          fill="none"
                                                          xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                          <circle
                                                            cx="9"
                                                            cy="9"
                                                            r="9"
                                                            fill="#1ED760"
                                                          />
                                                          <path
                                                            d="M13.5382 5.61448C13.471 5.54664 13.3909 5.4928 13.3027 5.45605C13.2145 5.41931 13.1199 5.40039 13.0244 5.40039C12.9288 5.40039 12.8342 5.41931 12.746 5.45605C12.6578 5.4928 12.5778 5.54664 12.5105 5.61448L7.11848 11.0137L4.85311 8.74113C4.78325 8.67365 4.70078 8.62058 4.61042 8.58497C4.52005 8.54936 4.42356 8.5319 4.32644 8.53358C4.22933 8.53526 4.1335 8.55605 4.04442 8.59476C3.95534 8.63348 3.87476 8.68936 3.80727 8.75922C3.73979 8.82908 3.68673 8.91155 3.65112 9.00191C3.6155 9.09228 3.59804 9.18877 3.59972 9.28589C3.6014 9.383 3.62219 9.47884 3.66091 9.56792C3.69963 9.65699 3.75551 9.73758 3.82537 9.80506L6.60461 12.5843C6.6719 12.6521 6.75194 12.706 6.84014 12.7427C6.92834 12.7795 7.02294 12.7984 7.11848 12.7984C7.21403 12.7984 7.30863 12.7795 7.39682 12.7427C7.48502 12.706 7.56507 12.6521 7.63235 12.5843L13.5382 6.67841C13.6117 6.61063 13.6703 6.52838 13.7104 6.43682C13.7505 6.34527 13.7713 6.2464 13.7713 6.14644C13.7713 6.04649 13.7505 5.94762 13.7104 5.85607C13.6703 5.76451 13.6117 5.68225 13.5382 5.61448Z"
                                                            fill="white"
                                                          />
                                                        </svg>

                                                        {b.featureName}
                                                      </div>
                                                    )
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })
                                      )}
                                    </div>
                                  </div>
                                  <div className="planExpireDate">
                                    <div className="innerPlanExpireDate">
                                      {!user?.free &&
                                        subscriptionInfo.status === 'trialing' &&
                                        subscriptionInfo.trialEnd > 0 && (
                                          <div
                                            style={{
                                              border: '1px solid #007AFE',
                                              borderRadius: '8px',
                                              padding: '12px 16px',
                                              marginBottom: '16px',
                                            }}
                                          >
                                            <p
                                              style={{
                                                color: '#007AFE',
                                                margin: 0,
                                                fontSize: '14px',
                                                fontWeight: 600,
                                              }}
                                            >
                                              🎉 You're on a 30-day free trial
                                            </p>
                                            <p
                                              style={{
                                                color: '#b8b8b8',
                                                margin: '6px 0 0 0',
                                                fontSize: '13px',
                                              }}
                                            >
                                              Trial ends:{' '}
                                              <strong style={{ color: '#fff' }}>
                                                {new Date(
                                                  subscriptionInfo.trialEnd * 1000
                                                ).toLocaleDateString('en-US', {
                                                  year: 'numeric',
                                                  month: 'long',
                                                  day: 'numeric',
                                                })}
                                              </strong>
                                            </p>
                                            <p
                                              style={{
                                                color: '#b8b8b8',
                                                margin: '4px 0 0 0',
                                                fontSize: '13px',
                                              }}
                                            >
                                              You won't be charged until then. Cancel anytime.
                                            </p>
                                          </div>
                                        )}
                                      {!user?.free ? (
                                        subscriptionInfo.cancelAtPeriodEnd ? (
                                          <>
                                            <div
                                              style={{
                                                backgroundColor: '#007AFE',
                                                border: '1px solid #007AFE',
                                                borderRadius: '8px',
                                                padding: '12px 16px',
                                                marginBottom: '16px',
                                              }}
                                            >
                                              <p
                                                style={{
                                                  color: '#fff',
                                                  margin: 0,
                                                  fontSize: '14px',
                                                  fontWeight: 500,
                                                }}
                                              >
                                                Your subscription is set to cancel on{' '}
                                                <strong>
                                                  {new Date(
                                                    subscriptionInfo.cancelAt * 1000
                                                  ).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric',
                                                  })}
                                                </strong>
                                              </p>
                                              <p
                                                style={{
                                                  color: '#fff',
                                                  margin: '8px 0 0 0',
                                                  fontSize: '13px',
                                                }}
                                              >
                                                You will retain full access until this date.
                                              </p>
                                            </div>
                                            <p>Access ends on</p>
                                            <h1>
                                              {new Date(
                                                subscriptionInfo.cancelAt * 1000
                                              ).toLocaleDateString()}
                                            </h1>
                                            <hr />
                                          </>
                                        ) : (
                                          <>
                                            {subscriptionInfo.status === 'trialing' ? (
                                              <></>
                                            ) : (
                                              <>
                                                <p>
                                                  {subscriptionInfo.status === 'trialing'
                                                    ? 'Plan charges on'
                                                    : 'Plan renews on'}
                                                </p>
                                                <h1>{expire_date}</h1>
                                                <hr />
                                              </>
                                            )}
                                          </>
                                        )
                                      ) : null}
                                      <div className="expireButtons">
                                        {(() => {
                                          const onHighestPlan =
                                            !user?.free &&
                                            getStripePlanType(
                                              subscriptionInfo.priceId || user?.planId
                                            ) === 'unlimited';
                                          // Highest plan: cancel only. Free / Pro / pending-cancel still get upgrade or reactivate.
                                          if (onHighestPlan && !subscriptionInfo.cancelAtPeriodEnd) {
                                            return null;
                                          }
                                          return (
                                            <button
                                              onClick={() => {
                                                setPrincingModalIsOpen(true);
                                              }}
                                              className="upgradePlans"
                                            >
                                              {user?.free
                                                ? 'Upgrade plan'
                                                : subscriptionInfo.cancelAtPeriodEnd
                                                  ? 'Reactivate'
                                                  : showAccount &&
                                                    showAccount.length > 0 &&
                                                    showAccount.slice(-1).map((e, index) => {
                                                      const amount = Number(
                                                        e?.payment_status?.subtotal
                                                      );
                                                      return monthly_pro_price.includes(amount) ||
                                                        monthly_unlimited_price.includes(amount) ||
                                                        annually_pro_price.includes(amount)
                                                        ? 'Upgrade plan'
                                                        : annually_unlimited_price.includes(amount)
                                                          ? 'Choose plan'
                                                          : 'Upgrade plan';
                                                    })}
                                            </button>
                                          );
                                        })()}
                                        {user?.free ? null : subscriptionInfo.cancelAtPeriodEnd ? (
                                          <button
                                            className="CancelPlans"
                                            style={{ opacity: 0.6, cursor: 'not-allowed' }}
                                            disabled
                                            title="Subscription is already scheduled for cancellation"
                                          >
                                            <svg
                                              width="21"
                                              height="21"
                                              viewBox="0 0 21 21"
                                              fill="none"
                                              xmlns="http://www.w3.org/2000/svg"
                                            >
                                              <path
                                                d="M10.5 2C5.80558 2 2 5.80558 2 10.5C2 15.1944 5.80558 19 10.5 19C15.1944 19 19 15.1944 19 10.5C19 5.80558 15.1944 2 10.5 2ZM10.5 17.5C6.63401 17.5 3.5 14.366 3.5 10.5C3.5 6.63401 6.63401 3.5 10.5 3.5C14.366 3.5 17.5 6.63401 17.5 10.5C17.5 14.366 14.366 17.5 10.5 17.5ZM11.25 10.5V6.75H9.75V10.5H11.25ZM11.25 14.25V12.75H9.75V14.25H11.25Z"
                                                fill="white"
                                              />
                                            </svg>
                                            Cancellation pending
                                          </button>
                                        ) : (
                                          <button
                                            onClick={() => showModalDelete(false)}
                                            className="CancelPlans"
                                          >
                                            <svg
                                              width="21"
                                              height="21"
                                              viewBox="0 0 21 21"
                                              fill="none"
                                              xmlns="http://www.w3.org/2000/svg"
                                            >
                                              <path
                                                d="M6.58301 17.5C6.12467 17.5 5.73245 17.3369 5.40634 17.0108C5.07967 16.6842 4.91634 16.2917 4.91634 15.8333V5C4.68023 5 4.48217 4.92028 4.32217 4.76083C4.16273 4.60083 4.08301 4.40278 4.08301 4.16667C4.08301 3.93056 4.16273 3.7325 4.32217 3.5725C4.48217 3.41306 4.68023 3.33333 4.91634 3.33333H8.24967C8.24967 3.09722 8.32967 2.89917 8.48967 2.73917C8.64912 2.57972 8.8469 2.5 9.08301 2.5H12.4163C12.6525 2.5 12.8505 2.57972 13.0105 2.73917C13.17 2.89917 13.2497 3.09722 13.2497 3.33333H16.583C16.8191 3.33333 17.0169 3.41306 17.1763 3.5725C17.3363 3.7325 17.4163 3.93056 17.4163 4.16667C17.4163 4.40278 17.3363 4.60083 17.1763 4.76083C17.0169 4.92028 16.8191 5 16.583 5V15.8333C16.583 16.2917 16.42 16.6842 16.0938 17.0108C15.7672 17.3369 15.3747 17.5 14.9163 17.5H6.58301ZM6.58301 5V15.8333H14.9163V5H6.58301ZM8.24967 13.3333C8.24967 13.5694 8.32967 13.7672 8.48967 13.9267C8.64912 14.0867 8.8469 14.1667 9.08301 14.1667C9.31912 14.1667 9.51717 14.0867 9.67717 13.9267C9.83662 13.7672 9.91634 13.5694 9.91634 13.3333V7.5C9.91634 7.26389 9.83662 7.06583 9.67717 6.90583C9.51717 6.74639 9.31912 6.66667 9.08301 6.66667C8.8469 6.66667 8.64912 6.74639 8.48967 6.90583C8.32967 7.06583 8.24967 7.26389 8.24967 7.5V13.3333ZM11.583 13.3333C11.583 13.5694 11.663 13.7672 11.823 13.9267C11.9825 14.0867 12.1802 14.1667 12.4163 14.1667C12.6525 14.1667 12.8505 14.0867 13.0105 13.9267C13.17 13.7672 13.2497 13.5694 13.2497 13.3333V7.5C13.2497 7.26389 13.17 7.06583 13.0105 6.90583C12.8505 6.74639 12.6525 6.66667 12.4163 6.66667C12.1802 6.66667 11.9825 6.74639 11.823 6.90583C11.663 7.06583 11.583 7.26389 11.583 7.5V13.3333Z"
                                                fill="white"
                                              />
                                            </svg>
                                            Cancel subscription
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="divider22">
                                <h5 className="htp5">Billing info</h5>
                                <br />
                                <p className="spaning">
                                  <span className="BillingInfoFontSize" style={{ color: 'grey' }}>
                                    Email:
                                  </span>{' '}
                                  <span className="spaning1 BillingInfoFontSize">
                                    {user?.planId === proPlan && user?.planId === unlimitedPlan
                                      ? showAccount &&
                                        showAccount.length > 0 &&
                                        showAccount.slice(-1).map((e, index) => {
                                          return e?.payment_status?.customer_email;
                                        })
                                      : user?.email}
                                  </span>
                                </p>
                                <br />
                                <p className="spaning">
                                  <span className="spaning1" style={{ marginLeft: '0px' }}>
                                    {showAccount &&
                                      showAccount.length > 0 &&
                                      showAccount.slice(-1).map((e, index) => {
                                        return (
                                          <span className="spanning1 BillingInfoFontSize">
                                            <span
                                              className="BillingInfoFontSize"
                                              style={{ color: 'grey' }}
                                            >
                                              Billing country:
                                            </span>

                                            <span className="spaning1 BillingInfoFontSize">
                                              {e?.payment_status?.customer_address?.country
                                                ? e?.payment_status?.customer_address?.country
                                                : 'no billing country'}
                                            </span>
                                          </span>
                                        );
                                      })}
                                  </span>
                                </p>
                              </div>
                              {user?.planId === proPlan || user?.planId === unlimitedPlan ? (
                                <div className="divider22">
                                  <h5 className="htp5">Payment method</h5>
                                  <br />
                                  <p className="spaning">
                                    {/* {getCardIcon(cardDetail.brand)} */}
                                    <span className="spaning1 BillingInfoFontSize">
                                      {cardDetail
                                        ? `${cardDetail.brand.toUpperCase()} *** ${
                                            cardDetail?.last4
                                          } `
                                        : ' '}
                                    </span>
                                    <span className="paymentType">Default</span>
                                  </p>
                                  <br />
                                  <p className="spaning">
                                    <svg
                                      style={{ marginRight: '8px' }}
                                      width="24"
                                      height="24"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M12 2C10.0222 2 8.08879 2.58649 6.4443 3.6853C4.79981 4.78412 3.51809 6.3459 2.76121 8.17317C2.00433 10.0004 1.8063 12.0111 2.19215 13.9509C2.578 15.8907 3.53041 17.6725 4.92894 19.0711C6.32746 20.4696 8.10929 21.422 10.0491 21.8079C11.9889 22.1937 13.9996 21.9957 15.8268 21.2388C17.6541 20.4819 19.2159 19.2002 20.3147 17.5557C21.4135 15.9112 22 13.9778 22 12C22 10.6868 21.7413 9.38642 21.2388 8.17317C20.7363 6.95991 19.9997 5.85752 19.0711 4.92893C18.1425 4.00035 17.0401 3.26375 15.8268 2.7612C14.6136 2.25866 13.3132 2 12 2ZM12 20C10.4178 20 8.87104 19.5308 7.55544 18.6518C6.23985 17.7727 5.21447 16.5233 4.60897 15.0615C4.00347 13.5997 3.84504 11.9911 4.15372 10.4393C4.4624 8.88743 5.22433 7.46197 6.34315 6.34315C7.46197 5.22433 8.88743 4.4624 10.4393 4.15372C11.9911 3.84504 13.5997 4.00346 15.0615 4.60896C16.5233 5.21447 17.7727 6.23984 18.6518 7.55544C19.5308 8.87103 20 10.4177 20 12C20 14.1217 19.1572 16.1566 17.6569 17.6569C16.1566 19.1571 14.1217 20 12 20Z"
                                        fill="white"
                                      />
                                      <path
                                        d="M12 9C12.5523 9 13 8.55228 13 8C13 7.44772 12.5523 7 12 7C11.4477 7 11 7.44772 11 8C11 8.55228 11.4477 9 12 9Z"
                                        fill="white"
                                      />
                                      <path
                                        d="M12 10C11.7348 10 11.4804 10.1054 11.2929 10.2929C11.1054 10.4804 11 10.7348 11 11V16C11 16.2652 11.1054 16.5196 11.2929 16.7071C11.4804 16.8946 11.7348 17 12 17C12.2652 17 12.5196 16.8946 12.7071 16.7071C12.8946 16.5196 13 16.2652 13 16V11C13 10.7348 12.8946 10.4804 12.7071 10.2929C12.5196 10.1054 12.2652 10 12 10Z"
                                        fill="white"
                                      />
                                    </svg>

                                    <span className="spaning1 BillingInfoFontSize">
                                      Expires on {cardDetail?.exp_month}/ {cardDetail?.exp_year}
                                    </span>
                                  </p>
                                </div>
                              ) : null}
                              <div className="divider22 invoice-history-table">
                                <h5 className="htp5 InvoiceHistory">Invoice history</h5>
                                <br />
                                <Table
                                  dataSource={dataSource}
                                  columns={columns}
                                  pagination={false}
                                />
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ),
              },
            ]}
          />
          <DeleteModal
            handleCancel={handleCancelDelete}
            showModal={() => showModalDelete(false)}
            isModalOpen={isModalOpenDelete}
            handleOk={() => handleOkDelete(true)}
            heading={
              cancelPaymentData
                ? 'Switch to the selected plan?'
                : 'Do you want to cancel subscription?'
            }
            headingText={''}
            button1={'Yes'}
            button2={'No'}
          />
        </div>
        <PricingModal
          next={(id, type, duration, currency) => {
            // First-ever paid plan must use card; credits only after hasCardPayment.
            const canPayWithCredits =
              type !== 'Free' && preferredBillingMethod === 'credits' && creditsAllowed;
            if (canPayWithCredits) {
              handleCreditsPlanChange(id, type, currency);
              return;
            }
            // Skip confirmation modal for free users or users who have already cancelled
            if (user?.free || subscriptionInfo.cancelAtPeriodEnd) {
              handlePricingSelection(id, type, duration, currency);
            } else {
              showModalDelete({
                id,
                type,
                duration,
                currency,
              });
            }
          }}
          isOpen={princingModalIsOpen}
          onClose={() => {
            setPrincingModalIsOpen(false);
            setWalletUpgradeBanner(null);
          }}
          showmenu={true}
          selectedCurrency={
            showAccount &&
            showAccount.length > 0 &&
            showAccount[showAccount.length - 1]?.payment_status?.currency.toUpperCase()
          }
          currentPlan={
            {
              free: 'Free',
              pro: 'Pro',
              unlimited: 'Unlimited',
            }[
              user?.free
                ? 'free'
                : getStripePlanType(subscriptionInfo.priceId || user?.planId)
            ]
          }
          currentPriceId={
            user?.free ? undefined : subscriptionInfo.priceId || user?.planId || undefined
          }
          actionsDisabled={walletUpgradeLoading}
          banner={
            <>
              {!creditsAllowed ? (
                <div
                  style={{
                    backgroundColor: '#1a2332',
                    color: '#f0c14b',
                    border: '1px solid #3d4a63',
                    padding: '10px 16px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    fontSize: '13px',
                    fontWeight: 600,
                    textAlign: 'center',
                  }}
                >
                  Note: Your first subscription must be paid by card. After that, you can use
                  available wallet credits for renewals and upgrades.
                </div>
              ) : null}
              {walletUpgradeBanner ? (
                <div
                  style={{
                    backgroundColor: '#EA4335',
                    color: '#fff',
                    padding: '10px 16px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    fontSize: '13px',
                    fontWeight: 600,
                    textAlign: 'center',
                  }}
                >
                  {walletUpgradeBanner}
                </div>
              ) : null}
            </>
          }
        />
      </>
    </GeneralInformationFormStyle>
  );
};

export default GeneralInformationForm;
